import React, {
    useCallback, useEffect, useState, FC,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { withMainLayout } from 'hocs';
import Preloader from 'components/Preloader';
import NewWrapper from 'components/AtomicStructure/layouts/NewWrapper';
import ButtonRadioElement from 'components/AtomicStructure/atoms/ButtonRadioElement';
import Error from 'components/AtomicStructure/atoms/Error';
import { getUserRegisterId } from 'pages/ClientOnboardingFlow/helpers/userRegisterId';
import { IsAllDataNull } from 'validation/validation';
import { confirmationPageLink, uploadDocumentsPageLink } from 'pages/ClientOnboardingFlow/helpers/links';
import SM from 'services/ServiceManager';
import { sendModel } from './models/sendModel';
import ButtonsBlockRow from '../../../../../components/ButtonsBlockRow';
import { Infobox, Modal, Title } from '../../../../../ui-library';

import './QuestionPage.css';

// Define the shape of your form data
interface FormData {
    customer: boolean;
    communicationPortal?: string | boolean;
}

// Define potential error keys based on your form fields
interface ErrorData {
    customer?: string;
    [key: string]: string | undefined;
}

const QuestionPage: FC = () => {
    const navigate = useNavigate();
    const { i18n, t } = useTranslation();

    const portalCustomer = [
        { label: i18n.t('clientDashboard.yes'), id: true },
        { label: i18n.t('clientDashboard.no'), id: false },
    ];

    const userRegisterId = getUserRegisterId();

    // State Hooks with Types
    const [isLoading, setLoading] = useState<boolean>(true);
    const [isSending, setSending] = useState<boolean>(false);
    const [data, setData] = useState<FormData>({ customer: true });
    const [errorPage, setErrorPage] = useState<string | undefined>();
    const [errorData, setErrorData] = useState<ErrorData>({});
    const [disableButton, setDisableButton] = useState<boolean>(true);

    const goBack = (): void => {
        navigate(uploadDocumentsPageLink);
    };

    const saveDataAndNavigate = useCallback(async (): Promise<void> => {
        try {
            setSending(true);
            // Assuming SM.clientService returns a Promise
            await SM.clientService('patchContact', [userRegisterId, sendModel(data)]);
            navigate(confirmationPageLink);
        } catch (err: any) {
            setErrorPage(err?.response?.data?.message || 'An error occurred');
        } finally {
            setSending(false);
        }
    }, [data, userRegisterId, navigate]);

    const onContinue = useCallback((): void => {
        if (IsAllDataNull(errorData) === false) {
            return;
        }

        if (data.customer) {
            saveDataAndNavigate();
        } else {
            Modal.confirm({
                title: t('onboardingFlow.questionPage.printDocsPopup.title'),
                content: <div>
                    {t('onboardingFlow.questionPage.printDocsPopup.desc')}
                    <br />
                    {t('onboardingFlow.questionPage.paperless.desc.noMortgage')}
                </div>,
                okText: t('onboardingFlow.questionPage.printDocsPopup.confirm'),
                cancelText: t('onboardingFlow.questionPage.printDocsPopup.cancel'),
                onCancel: () => {},
                onOk: saveDataAndNavigate,
            });
        }
    }, [saveDataAndNavigate, errorData, data.customer, t]);

    const passData = (value: any, key: keyof FormData): void => {
        setData((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    useEffect(() => {
        setLoading(false);
    }, []);

    // Sync communicationPortal based on customer selection
    useEffect(() => {
        if (data.customer === true) {
            passData('communicationPortal', 'communicationPortal');
        } else {
            passData(false, 'communicationPortal');
        }
    }, [data.customer]);

    // Validation for button state
    useEffect(() => {
        if (Object.prototype.hasOwnProperty.call(data, 'customer')) {
            setDisableButton(false);
        } else {
            setDisableButton(true);
        }
    }, [data]);

    return (
        <NewWrapper
            stepNavBarActive={2}
            headTitle={t('onboardingFlow.customerOnboarding.title')}
        >
            <div className="newStyle QuestionPage">
                <div className="newStyleContent">
                    <div className="container">
                        <Preloader isLoading={isLoading} error={null}>
                            <Title type={2}>
                                {t('onboardingFlow.step.quarter')}
                            </Title>
                            <div className="form">
                                <div>
                                    <ButtonRadioElement
                                        label={t('onboardingFlow.questionPage.customerPortalQuestion')}
                                        items={portalCustomer}
                                        value={data.customer}
                                        nameData="customer"
                                        passData={passData}
                                        error={errorData.customer}
                                    />
                                </div>
                            </div>
                            {data.customer && (
                                <Infobox className="infobox">
                                    <Title type={4}>
                                        {t('onboardingFlow.questionPage.paperless.title')}
                                    </Title>
                                    {t('onboardingFlow.questionPage.paperless.desc')}
                                    <br />
                                    {t('onboardingFlow.questionPage.paperless.desc.noMortgage')}
                                </Infobox>
                            )}
                        </Preloader>
                    </div>
                    <div className="JourneyFooter">
                        <div className="container">
                            {errorPage && <Error>{errorPage}</Error>}
                            <ButtonsBlockRow
                                additionalButton={{
                                    text: t('onboardingFlow.documetsPage.back'),
                                    onClick: goBack,
                                }}
                                primaryButton={{
                                    text: t('onboardingFlow.clientsDataPage.continue'),
                                    onClick: onContinue,
                                    disabled: disableButton,
                                    loading: isSending,
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </NewWrapper>
    );
};

export default withMainLayout(QuestionPage);
